import React, { useEffect, useId, useRef, useState } from "react";
import "./App.css";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "./images/1_1_logo_XPER_blue_woBg 1.svg";
import HeadRoom from "react-headroom";
import ClickAwayListener from "react-click-away-listener";
import { Menu, X, Globe, ChevronDown } from "lucide-react";

const focusStyle = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-main";
const dropdownStyle = "flex flex-col bg-gray lg:absolute left-1/2 lg:-translate-x-1/2 lg:mt-7 text-main-dark py-4 px-8 gap-2 min-w-[100px]";

function Disclosure({ label, children, triggerRef, id }) {
  const [open, setOpen] = useState(false);
  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <li className="relative" onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.stopPropagation();
          setOpen(false);
          triggerRef.current?.focus();
        }
      }}>
        <button type="button" ref={triggerRef} aria-expanded={open} aria-controls={id}
          className={`min-h-6 hover:underline ${focusStyle}`}
          onClick={() => setOpen((value) => !value)}>{label}</button>
        {open && <ul id={id} className={dropdownStyle}>
          {children(() => setOpen(false))}
        </ul>}
      </li>
    </ClickAwayListener>
  );
}

export default function Nav() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const productsRef = useRef(null);
  const languageRef = useRef(null);
  const menuId = useId();
  const productsId = useId();
  const languageId = useId();
  const english = i18n.language?.startsWith("en");
  useEffect(() => {
    document.documentElement.lang = english ? "en" : "no";
  }, [english]);
  const navLink = (to, key, close = () => {}) => (
    <li key={to}>
      <NavLink to={to} className={`inline-block min-h-6 hover:underline ${focusStyle}`}
        onClick={() => { close(); setOpen(false); }}>{t(key)}</NavLink>
    </li>
  );
  return (
    <HeadRoom wrapperStyle={{ zIndex: 100, position: "relative" }}>
      <header>
        <nav aria-label={english ? "Main navigation" : "Hovedmeny"} className="bg-white text-main-dark"
          onKeyDown={(event) => {
            if (event.key === "Escape" && open) {
              setOpen(false);
              menuRef.current?.focus();
            }
          }}>
          <div className="container flex flex-col lg:flex-row max-w-2xl items-center py-4 whitespace-nowrap gap-5">
            <div className="flex justify-between items-center w-full lg:w-auto">
              <NavLink to="/" className={focusStyle} onClick={() => setOpen(false)}>
                <img src={logo} alt="XPER logo" className="h-12 lg:w-64" />
              </NavLink>
              <button type="button" ref={menuRef} aria-controls={menuId} aria-expanded={open}
                aria-label={english ? (open ? "Close menu" : "Open menu") : (open ? "Lukk meny" : "Åpne meny")}
                onClick={() => setOpen((value) => !value)} className={`lg:hidden p-3 ${focusStyle}`}>
                {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
              </button>
            </div>
            <div id={menuId} className={`lg:flex flex-col lg:flex-row items-center w-full gap-5 ${open ? "flex" : "hidden"}`}>
              <ul className="flex flex-col lg:flex-row items-center w-full gap-5">
                <Disclosure label={t("nav.products")} triggerRef={productsRef} id={productsId}>
                  {(close) => [navLink("/docengine", "nav.docengine", close), navLink("/ccm", "nav.ccm", close), navLink("/migrator", "nav.migrator", close)]}
                </Disclosure>
                {navLink("/artikler", "nav.articles")}
                {navLink("/kunder", "nav.customers")}
                {navLink("/partnere", "nav.partners")}
              </ul>
              <ul className="flex flex-col lg:flex-row items-center gap-5">
                {navLink("/om-xper", "nav.about")}
                <Disclosure triggerRef={languageRef} id={languageId} label={
                  <span className="flex items-center gap-1">
                    <Globe size={16} aria-hidden="true" />
                    <span className="sr-only">{english ? "Language: " : "Språk: "}</span>
                    {english ? "EN" : "NO"}<ChevronDown size={16} aria-hidden="true" />
                  </span>
                }>
                  {(close) => ["no", "en"].map((language) => (
                    <li key={language}>
                      <button type="button" lang={language} className={`min-h-6 hover:underline ${focusStyle}`}
                        onClick={() => {
                          i18n.changeLanguage(language);
                          close();
                          languageRef.current?.focus();
                        }}>{language === "no" ? "Norsk" : "English"}</button>
                    </li>
                  ))}
                </Disclosure>
                <li><NavLink to="/skjema/kontakt-oss" onClick={() => setOpen(false)}
                  className={`inline-block rounded-md bg-main text-white hover:bg-main-dark px-4 py-2 font-semibold ${focusStyle}`}>
                  {t("nav.contact")}
                </NavLink></li>
              </ul>
            </div>
          </div>
        </nav>
      </header>
    </HeadRoom>
  );
}
